import { useCallback, useState } from "react";
import { uploadTemplate, UploadTemplateResponse } from "./uploadTemplate";

export interface UseUploadTemplateState {
  isLoading: boolean;
  error: string | null;
  data: UploadTemplateResponse | null;
  isSuccess: boolean;
}

export interface UseUploadTemplateReturn extends UseUploadTemplateState {
  upload: (file: File) => Promise<UploadTemplateResponse | null>;
  reset: () => void;
}

export const useUploadTemplate = (
  quoteId: string,
  itineraryId: string,
  isExcelBypass: boolean
): UseUploadTemplateReturn => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<UploadTemplateResponse | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);

  const reset = useCallback(() => {
    setIsLoading(false);
    setError(null);
    setData(null);
    setIsSuccess(false);
  }, []);

  const upload = useCallback(
    async (file: File): Promise<UploadTemplateResponse | null> => {
      setIsLoading(true);
      setError(null);
      setData(null);
      setIsSuccess(false);

      try {
        const response = await uploadTemplate(
          file,
          quoteId,
          itineraryId,
          isExcelBypass
        );
        setData(response);
        setIsSuccess(true);
        return response;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to upload template";
        setError(errorMessage);
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  return {
    isLoading,
    error,
    data,
    isSuccess,
    upload,
    reset,
  };
};
